import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import type { FormEventHandler, ReactNode } from 'react';
import { useLeaveGuard } from '../unsaved/unsaved';

export interface FormDialogProps {
  title: string;
  /** One line under the title. */
  lead?: ReactNode;
  submitLabel: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onClose(): void;
  busy?: boolean;
  /** A problem with the form as a whole (field problems show on the fields). */
  error?: string;
  children: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md';
  /** An exact widest size in pixels, between the standard ones (e.g. 760). */
  width?: number;
  danger?: boolean;
  /** Tabs shown under the title (they stay put while the content scrolls). */
  tabs?: ReactNode;
  /** Keeps the dialog the same height as you switch tabs. */
  minContentHeight?: number;
  /** For people who may look but not change: no save button, just Close. */
  readOnly?: boolean;
  /** Extra buttons on the left of the footer (e.g. Deactivate). */
  secondaryActions?: ReactNode;
}

/**
 * The standard dialog with a form: title, fields, Cancel and one main button. Full
 * screen on phones. The buttons are at the end of the fields, so a long form is scrolled through to reach them. Enter submits; Escape or Cancel closes (not while saving), asking first about unsaved changes.
 */
export function FormDialog({ title, lead, submitLabel, onSubmit, onClose: close, busy, error, children, maxWidth = 'sm', width, danger, tabs, minContentHeight, readOnly, secondaryActions }: FormDialogProps) {
  const phone = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  // Cancel, Close, Escape and clicking outside all ask first if something is unsaved.
  const guard = useLeaveGuard();
  const onClose = () => guard.close(close);
  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={phone}
      aria-labelledby="form-dialog-title"
      slotProps={{ paper: { component: 'form', onSubmit: (e: React.FormEvent<HTMLFormElement>) => { e.preventDefault(); onSubmit(e); }, noValidate: true, ...(width && !phone ? { sx: { maxWidth: width } } : {}) } as object }}
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      {tabs && <Box sx={{ px: 3 }}>{tabs}</Box>}
      <DialogContent sx={{ minHeight: { sm: minContentHeight }, pt: tabs ? '24px !important' : undefined, pb: 3, display: 'flex', flexDirection: 'column' }}>
        {lead && (
          <Typography variant="body1" color="textSecondary" component="div" sx={{ mb: 2.5 }}>
            {lead}
          </Typography>
        )}
        <Stack spacing={2.5} sx={{ pt: lead ? 0 : 1 }}>
          {children}
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2.5 }} role="alert">
            {error}
          </Alert>
        )}
        {/* The buttons scroll with the fields, so the last field is seen before Save. */}
        <DialogActions sx={{ px: 0, pb: 0, pt: 3, mt: 'auto' }}>
        {secondaryActions && <Box sx={{ mr: 'auto', display: 'flex', gap: 1.5 }}>{secondaryActions}</Box>}
        {readOnly ? (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color={danger ? 'error' : 'primary'} disabled={busy}>
              {busy ? 'Saving…' : submitLabel}
            </Button>
          </>
        )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
